import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useCurrency, useCustomer, useSettings } from '@backpackjs/storefront';
import {
  sendShopifyAnalytics,
  getClientBrowserParameters,
  AnalyticsEventName,
} from '@shopify/hydrogen-react';

import { useShopifyAnalyticsAddToCart } from './useShopifyAnalyticsAddToCart';
import { useShopifyAnalyticsPageView } from './useShopifyAnalyticsPageView';
import { readShopifyConsent, refreshShopifyTracking } from './shopifyTracking';
import { useShopifyConsent } from './useShopifyConsent';

// Envs to set:
// * NEXT_PUBLIC_SHOPIFY_SHOP_ID // see readme on where to obtain
// * NEXT_PUBLIC_SITE_TITLE // provides brand name

const DEBUG = true;

const sendShopifyAnalyticsEvent = async (
  analyticsPageData,
  eventName,
  callback,
  isEnabled
) => {
  try {
    if (!AnalyticsEventName[eventName] || !isEnabled()) return;
    // Navigation can finish while tracking initializes. Capture this event's
    // browser context now, then replace its identity with server-issued values.
    const browserParameters = getClientBrowserParameters();
    const tracking = await refreshShopifyTracking();
    const consent = readShopifyConsent();
    if (!tracking || !consent?.analyticsAllowed || !isEnabled()) return;
    const payload = {
      ...browserParameters,
      ...analyticsPageData,
      ...tracking,
      hasUserConsent: consent.analyticsAllowed,
      analyticsAllowed: consent.analyticsAllowed,
      marketingAllowed: consent.marketingAllowed,
      saleOfDataAllowed: consent.saleOfDataAllowed,
    };
    const event = {
      eventName: AnalyticsEventName[eventName],
      payload,
    };

    await sendShopifyAnalytics(event);
    if (typeof callback === 'function') callback(event);
    if (DEBUG) console.log('sendShopifyAnalytics:event', event);
  } catch (error) {
    console.error('sendShopifyAnalyticsEvent:error', error.message);
  }
};

export function useShopifyAnalytics({ collection, product }) {
  const currency = useCurrency();
  const customer = useCustomer();
  const settings = useSettings();
  const { enabled } = { ...settings?.analytics };
  const consent = useShopifyConsent();
  const hasUserConsent = consent?.analyticsAllowed;
  useEffect(() => {
    if (!enabled || !consent) return undefined;
    let active = true;
    let failed = false;
    // Synchronize every privacy change without creating an analytics event.
    // Initial page/cart requests share this same in-flight synchronization.
    const synchronize = () => {
      failed = false;
      refreshShopifyTracking({ syncConsent: true }).catch(() => {
        if (!active) return;
        failed = true;
        console.error('Shopify consent cookie synchronization failed');
      });
    };
    const retryFailed = () => {
      if (failed) synchronize();
    };
    window.addEventListener('focus', retryFailed);
    window.addEventListener('pageshow', retryFailed);
    synchronize();
    return () => {
      active = false;
      window.removeEventListener('focus', retryFailed);
      window.removeEventListener('pageshow', retryFailed);
    };
  }, [enabled, consent]);
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;
  const sendEvent = useCallback(
    (payload, eventName, callback) =>
      sendShopifyAnalyticsEvent(
        payload,
        eventName,
        callback,
        () => enabledRef.current
      ),
    []
  );

  const customerPending = typeof customer === 'undefined';

  const basePayload = useMemo(() => {
    if (
      !currency ||
      customerPending ||
      !enabled ||
      !hasUserConsent ||
      !process.env.NEXT_PUBLIC_SHOPIFY_SHOP_ID
    )
      return null;

    return {
      hasUserConsent,
      shopId: `gid://shopify/Shop/${process.env.NEXT_PUBLIC_SHOPIFY_SHOP_ID}`,
      currency,
      acceptedLanguage: 'en',
      shopifySalesChannel: 'headless',
      ...(customer ? { customerId: customer.id } : null),
    };
  }, [currency, customer, enabled, hasUserConsent]);

  useShopifyAnalyticsPageView({
    basePayload,
    collection,
    product,
    sendShopifyAnalyticsEvent: sendEvent,
  });

  useShopifyAnalyticsAddToCart({
    basePayload,
    sendShopifyAnalyticsEvent: sendEvent,
  });
}
